import React, { useState, useEffect, useCallback } from "react";
import { Button, IconButton, Input, Progress, Spinner, Tooltip, Typography } from "@material-tailwind/react";
import { ArmchairIcon, ArrowDownIcon, ArrowUpIcon, Edit2Icon, EyeIcon, MailIcon, PhoneIcon, ScanQrCodeIcon } from "lucide-react";
import moment from "moment";
import { useGetUsers } from "../../custom-hooks/use-users";
import CustomerFormModal from "./customerFormModal";
import { STATIC_USER_ROLES } from "../../utils/enums";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { convertToPresentableText, formatNumberWithCommas, priceToWords } from "../../utils/functions";
import { GetAvatarAndTextColor } from "../../utils/ui-interpreters";
import { useGetCustomersStats } from "../../custom-hooks/use-stats";
import Pagination from "../../components/pagination";
import { PageHeader } from "../../components";
import { usePermissions } from "../../custom-hooks/use-permissions";

const INITIAL_FORM_DATA = {
  _id: '',
  email: '',
  password: '',
  name: '',
  role: 'user',
  cnic: '',
  phone: ''
};

const UserPage = () => {
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const paramsPage = params.get('page');
  const paramsLimit = params.get('limit');
  const paramsSort = params.get('sort');
  const paramsSearch = params.get('search');


  // State variables
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  // Pagination state
  const [page, setPage] = useState(paramsPage ? parseInt(paramsPage) : 1);
  const [limit, setLimit] = useState(paramsLimit ? parseInt(paramsLimit) : 10);
  // sorting
  const [sort, setSort] = useState(paramsSort ? paramsSort : "-createdDate");

  const [search, setSearch] = useState(paramsSearch ? paramsSearch : '');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Filtering
  // For single-active filter in params as "filter_KEY", separated from other params
  // Automatically parse filter value to number, boolean, or string as appropriate
  const parseFilterValue = useCallback((value, filterType) => {
    if (filterType === "number") return Number(value);
    if (filterType === "boolean") return value === "true";
    return value;
  }, []);

  const parseSingleFilterFromParams = useCallback((params) => {
    for (const [key, value] of params.entries()) {
      if (key.startsWith("f:")) {
        const [filterKey, filterType] = key.substring(2).split(":");
        return { [filterKey]: parseFilterValue(value, filterType) };
      }
    }
    return {};
  }, [parseFilterValue]);

  const [filters, setFilters] = useState(parseSingleFilterFromParams(params));

  // Keep filters synced with params
  useEffect(() => {
    setFilters(parseSingleFilterFromParams(params));
  }, [params, parseSingleFilterFromParams]);

  // Helper for setting a filter (only one active filter at a time)
  const handleFilterChange = (filterKey, filterValue) => {
    const newParams = new URLSearchParams(params.toString());

    // Remove all existing filters
    [...newParams.keys()].forEach((key) => {
      if (key.startsWith("f:")) {
        newParams.delete(key);
      }
    });

    if (filterKey && filterValue !== undefined && filterValue !== null && filterValue !== "") {
      newParams.set(`f:${filterKey}:${typeof filterValue}`, String(filterValue));
    }
    // Always go to first page on filter change
    newParams.set("page", 1);
    setPage(1);

    navigate(`/customer?${newParams.toString()}`);
    // setFilters will be handled by useEffect above
  };


  // Debounce the search input to avoid making API calls on every keystroke
  useEffect(() => {
    setIsTyping(true);
    const timer = setTimeout(() => {
      params.set('search', search);
      // Reset to page 1 when search changes
      if (search) {
        params.set('page', 1);
        setPage(1);
      }
      navigate(`/customer?${params.toString()}`);
      setDebouncedSearch(search);
      setIsTyping(false);
    }, 500); // Wait for 500ms after the customer stops typing

    return () => {
      clearTimeout(timer);
    };
  }, [search, params, page, navigate]);

  // useEffect(() => {
  //   params.set('page', 1);
  //   navigate(`/customer?${params.toString()}`);
  //   setPage(1);
  // }, [debouncedSearch, filters, params, navigate]);

  // Custom hooks
  const {
    data: list,
    isLoading: isUsersLoading,
    error: usersError,
  } = useGetUsers({
    role: STATIC_USER_ROLES.USER,
    _page: page,
    _limit: limit,
    _search: debouncedSearch,
    _sort: sort,
    _details: true, // Include details(stats) in the response
    ...filters
  });

  const {
    total,
    totalPages
  } = list || {};

  const {
    data: customersStats,
    isLoading: isCustomersStatsLoading,
    error: customersStatsError,
  } = useGetCustomersStats();

  // Open modal for adding a new customer
  const openAddUserModal = () => {
    setModalOpen(true);
    setFormData(INITIAL_FORM_DATA);
    setIsUpdating(false);
  };

  // Open modal for updating a customer
  const openUpdateUserModal = (customer) => {
    setModalOpen(true);
    setFormData({
      ...customer,
      password: '' // Clear password field for security
    });
    setIsUpdating(true);
    setSelectedUserId(customer._id);
  };

  // Handle search 
  const handleSearch = (e) => {
    setSearch(e.target.value);
  }

  const TABLE_COLUMNS = [
    {
      label: 'Name',
      key: 'name',
      sortable: true,
      sort: sort === "name" ? "-name" : "name"
    },
    {
      label: 'Phone',
      key: 'phone',
      sortable: true,
      sort: sort === "phone" ? "-phone" : "phone"
    },
    {
      label: 'Offices and Seats',
      key: 'officesAndSeats',
      sortable: false,
      sort: sort === "officesAndSeats" ? "-officesAndSeats" : "officesAndSeats"
    },
    {
      label: 'Monthly Rent',
      key: 'monthlyRent',
      sortable: false,
      sort: sort === "monthlyRent" ? "-monthlyRent" : "monthlyRent"
    },
    {
      label: 'Space Used',
      key: 'meetingRoomQuota',
      sortable: true,
      sort: sort === "meetingRoomQuota" ? "-meetingRoomQuota" : "meetingRoomQuota"
    },
    {
      label: 'Created Date',
      key: 'createdDate',
      sortable: true,
      sort: sort === "createdDate" ? "-createdDate" : "createdDate"
    },
    {
      label: 'Last Updated',
      key: 'lastUpdated',
      sortable: true,
      sort: sort === "lastUpdated" ? "-lastUpdated" : "lastUpdated"
    },
    {
      label: 'Actions',
      key: 'actions',
      sortable: false,
      sort: null,
      hidden: !(can('FX12-1', 'R') || can('FX12', 'edit'))
    }
  ].filter(col => !col.hidden);

  const statsCards = !customersStatsError && customersStats ?
    Object.entries(customersStats).map(([key, data]) => {
      // console.log(filters, data.filter, data.filterValue);
      return (
        <Button
          key={key}
          variant={"text"}
          className={`border-orange-300 border text-orange-500 shadow-none hover:bg-orange-50 duration-300 px-2 py-1 flex items-center gap-1 ${filters[data.filter] === data.filterValue ? 'bg-orange-50' : ''}`}
          // size="sm"
          style={{ textTransform: 'none' }}
          onClick={() => handleFilterChange(data.filter, data.filterValue)}
        >
          <span className="text-sm font-normal">#{convertToPresentableText(key)}</span>
          <span className="font-bold">{data.value}</span>
        </Button>
      )
    }) : [];

  const handleTableHeaderClick = (column) => {
    if (column.sortable) {
      setSort(column.sort);
      params.set('sort', column.sort);
      navigate(`/customer?${params.toString()}`);
    }
  }

  return (
    <div className="min-h-screen p-5 flex flex-col justify-between">
      <div className="p-5 border rounded-3xl space-y-4">
        <PageHeader
          title="Customer Management"
          isLoading={isUsersLoading}
          actionOnClick={openAddUserModal}
          actionType={can('FX12', 'edit') ? "add" : "none"}
          actionText="Add New Customer"
        />
        <Input
          label={`Search by Name, Email or Phone ${isTyping ? '(typing...)' : ''}`}
          value={search}
          onChange={handleSearch}
          icon={isTyping ? <Spinner className="h-4 w-4" /> : null}
        />

        <div className={`hidden md:flex gap-2`}>
          {isCustomersStatsLoading ? <Spinner /> : statsCards}
          <Button variant="text" size="sm" onClick={() => handleFilterChange(null, null)}>
            <span className="text-sm font-normal">Clear Filters</span>
          </Button>
        </div>
        {
          isUsersLoading ?
            <div className="flex justify-center py-10">
              <Spinner />
            </div> :
            (
              <div className="overflow-x-auto">
                <table className="w-full min-w-max table-auto text-left">
                  <thead className="text-sm">
                    <tr className="py-3">
                      <th colSpan={6} className="py-3 text-xs font-light text-orange-900">
                        List of Customers - showing {list?.users.length} of {total}
                      </th>
                    </tr>
                    <tr className="capitalize px-1">
                      {
                        TABLE_COLUMNS.map((column, index) => (
                          <th
                            key={index}
                            className={"cursor-pointer py-3"}
                            onClick={() => handleTableHeaderClick(column)}
                          >
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="flex items-center gap-2 font-normal leading-none opacity-70">
                              {column.label} {column.sortable ? (column.sort.startsWith('-') ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />) : ''}
                            </Typography>
                          </th>
                        ))
                      }
                    </tr>
                  </thead>
                  <tbody>
                    {
                      usersError ? (
                        <tr>
                          <td colSpan={6} className="text-center text-red-500">
                            {usersError.message}
                          </td>
                        </tr>
                      ) : (
                        list?.users.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="py-6 text-center text-gray-500">
                              No customers found.
                            </td>
                          </tr>
                        ) : (
                          list?.users.map((customer, index) => {
                            const { bg, text } = GetAvatarAndTextColor(customer.name);
                            const usedPercentage = (customer.meetingRoomQuota.usedHours / customer.meetingRoomQuota.monthlyHours) * 100;
                            return (
                              <tr key={index} className="">
                                <td className="font-thin py-3 text-sm px-1">
                                  <div className="flex gap-1">
                                    <div className="">
                                      <div className={`w-10 h-10 rounded-full ${bg} border flex items-center justify-center`}>
                                        {customer.name.split(' ').map((name, index) => (
                                          <span key={index} className={`text-xs font-bold ${text}`}>
                                            {name.charAt(0)}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="font-bold">
                                        {customer.name}
                                      </div>
                                      <div className="text-xs text-gray-700">
                                        <a href={`mailto:${customer.email}`} className="flex items-center gap-1 text-blue-600 hover:text-orange-900 duration-300">
                                          <MailIcon size={12} />
                                          {customer.email}
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="font-thin py-3 text-xs px-1">
                                  <Tooltip content={`Click to call ${customer.name}`}>
                                    <div className="flex items-center gap-2">
                                      <a href={`tel:${customer.phone}`} className="flex items-center gap-1 text-blue-600 hover:text-orange-900 duration-300">
                                        <PhoneIcon size={12} />
                                        {customer.phone}
                                      </a>
                                    </div>
                                  </Tooltip>
                                </td>
                                <td className="font-thin py-3 text-sm px-1">

                                  <div className="flex gap-2">
                                    <div className="flex items-center gap-1 w-24">
                                      <ScanQrCodeIcon size={14} />
                                      <span className={`text-gray-700 ${customer.stats?.occupiedOfficesCount > 0 ? 'font-bold' : ''}`}>Offices</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span className={`text-gray-700 ${customer.stats?.occupiedOfficesCount > 0 ? 'font-bold' : ''}`}>{customer.stats?.occupiedOfficesCount}</span>
                                    </div>

                                  </div>
                                  <div className="flex gap-2">
                                    <div className="flex items-center gap-1 w-24">
                                      <ArmchairIcon size={14} />
                                      <span className={`text-gray-700 ${customer.stats?.occupiedSeatsCount > 0 ? 'font-bold' : ''}`}>Seats</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span className={`text-gray-700 ${customer.stats?.occupiedSeatsCount > 0 ? 'font-bold' : ''}`}>{customer.stats?.occupiedSeatsCount}</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="font-thin py-3 text-sm px-1">
                                  <Tooltip content={priceToWords(customer.stats?.monthlyRevenue)}>
                                    <div className="flex items-center gap-1">
                                      <span className="text-orange-900 font-bold">
                                        PKR {formatNumberWithCommas(customer.stats?.monthlyRevenue)}
                                      </span>
                                    </div>
                                  </Tooltip>
                                </td>
                                <td className="font-thin py-3 text-sm px-1">
                                  <Tooltip content={`${customer.meetingRoomQuota.usedHours / 60} / ${customer.meetingRoomQuota.monthlyHours / 60} hours`}>
                                    <div className="flex items-center gap-1">
                                      <span className={`${usedPercentage > 70 ? 'text-red-900' : usedPercentage > 20 ? 'text-yellow-900' : 'text-blue-900'} font-bold`}>
                                        {usedPercentage.toFixed(0)}%
                                      </span>
                                    </div>
                                  </Tooltip>
                                  <Progress
                                    value={usedPercentage}
                                    color={usedPercentage > 70 ? "red" : usedPercentage > 20 ? "yellow" : "blue"}
                                    // label={`Used`}
                                    className='w-full'
                                  />
                                </td>
                                <td className="font-thin py-3 text-xs px-1">
                                  <div>
                                    <span className="text-gray-700">{moment(customer.createdDate).format('lll')}</span>
                                  </div>
                                  <div>
                                    By {
                                      customer.createdBy ?
                                        <Link to={`/user/${customer.createdBy?._id}`} className="hover:text-orange-900">{customer.createdBy?.name}</Link>
                                        : 'System Admin'
                                    }
                                  </div>
                                </td>
                                <td className="font-thin py-3 text-xs px-1">
                                  <div>
                                    <span className="text-gray-700">{moment(customer.lastUpdated).format('lll')}</span>
                                  </div>
                                  <div>
                                    By {
                                      customer.updatedBy ?
                                        <Link to={`/user/${customer.updatedBy?._id}`} className="hover:text-orange-900">{customer.updatedBy?.name}</Link>
                                        : 'System Admin'
                                    }
                                  </div>
                                </td>
                                {(can('FX12-1', 'R') || can('FX12', 'edit')) && (
                                <td className="py-3 px-1">
                                  <div className={`flex gap-1`}>
                                    {can('FX12-1', 'R') && (
                                    <Link to={`/customer/${customer._id}`}>
                                      <IconButton
                                        size="sm"
                                        className="rounded-full"
                                        color="blue"
                                      >
                                        <EyeIcon size={12} />
                                      </IconButton>
                                    </Link>
                                    )}
                                    {can('FX12', 'edit') && (
                                    <IconButton
                                      size="sm"
                                      color="orange"
                                      className="rounded-full"
                                      onClick={() => openUpdateUserModal(customer)}
                                    >
                                      <Edit2Icon size={12} />
                                    </IconButton>
                                    )}
                                  </div>
                                </td>
                                )}
                              </tr>
                            )
                          })))}
                  </tbody>
                </table>
                {
                  totalPages > 1 &&
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    limit={limit}
                    onChangePage={(page) => {
                      params.set('page', page);
                      navigate(`/customer?${params.toString()}`);
                      setPage(page);
                    }}
                    onChangeLimit={(limit) => {
                      params.set('limit', limit);
                      navigate(`/customer?${params.toString()}`);
                      setLimit(limit);
                    }}
                  />
                }
              </div>)
        }
      </div>

      {
        modalOpen &&
        <CustomerFormModal
          isOpen={modalOpen}
          onClose={setModalOpen}
          defaultData={formData}
          isUpdating={isUpdating}
          updatingUserId={selectedUserId}  // Passing selected customer ID for updating purpose
        />
      }
    </div>
  );
};

export default UserPage;